import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ActivityType } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import { kindLabel } from "../lib/kindLabel";
import { dateTimeLabel, localeOf } from "../lib/heatmap";
import { foreignLabel, hoursOf, moneyLabel, nativeLabel, sumCost, type CountedLine, type Money, type PersonSubtotal } from "../lib/ledger";
import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";

// ---------------------------------------------------------------------------
// Piezas comunes de los desgloses del tablero (#1344): el renglón que se abre,
// el importe y la tabla de líneas (quién, cuándo, qué, cuántas horas y cuánto
// costó). El costo solo se pinta si el servidor lo mandó (`costs`): lleva
// sueldos y solo lo reciben los dueños.
// ---------------------------------------------------------------------------

/** Importe de un total. «—» sin importes; con asterisco si falta el sueldo de alguien. */
export function MoneyText({ money, className = "" }: { money: Money; className?: string }) {
  const { t, i18n } = useTranslation("common");
  if (money.empty && !money.incomplete) return <span className={`${MONEY_PRIVACY_CLASS} ${className}`}>—</span>;
  return (
    <span className={`${MONEY_PRIVACY_CLASS} ${className}`} title={money.incomplete ? t("workHours.ledger.incompleteHint") : undefined}>
      {moneyLabel(money, i18n?.language)}
      {money.incomplete && <span className="text-amber-600"> *</span>}
    </span>
  );
}

interface ExpandRowProps {
  open: boolean;
  onToggle: () => void;
  /** Sin detalle que abrir (aún cargando o renglón vacío): se pinta sin botón. */
  disabled?: boolean;
  label: ReactNode;
  right: ReactNode;
  children?: ReactNode;
  /** Va debajo del renglón SIEMPRE (abierto o no, con o sin detalle): p. ej. un enlace, que no puede ir dentro del botón. */
  footer?: ReactNode;
  testId?: string;
  tc: ThemeClasses;
}

/** Un renglón que se despliega: etiqueta a la izquierda, cifras a la derecha, detalle debajo. */
export function ExpandRow({ open, onToggle, disabled = false, label, right, children, footer, testId, tc }: ExpandRowProps) {
  return (
    <div className={`border-t ${tc.border}`} data-testid={testId}>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={open}
        className={`flex w-full items-start justify-between gap-3 py-2 text-left text-sm ${tc.text} ${disabled ? "cursor-default" : tc.buttonHover}`}
      >
        <span className="flex min-w-0 items-start gap-1.5">
          <span className={`mt-0.5 w-3 shrink-0 text-xs ${tc.textSecondary}`} aria-hidden>
            {disabled ? "" : open ? "▾" : "▸"}
          </span>
          <span className="min-w-0">{label}</span>
        </span>
        <span className="shrink-0 text-right">{right}</span>
      </button>
      {footer && <div className="-mt-1 pb-1.5 pl-4">{footer}</div>}
      {open && !disabled && <div className="pb-3 pl-4">{children}</div>}
    </div>
  );
}

/** Horas y, si hay costos, su importe debajo. */
export function HoursAndMoney({ minutes, money, costs, tc }: { minutes: number; money: Money; costs: boolean; tc: ThemeClasses }) {
  return (
    <>
      <span className="font-semibold">{hoursOf(minutes)}</span>
      {costs && <MoneyText money={money} className={`block text-xs ${tc.textSecondary}`} />}
      {costs && <ForeignPart money={money} className={tc.textSecondary} />}
    </>
  );
}

/**
 * Bajo un total ya convertido a MXN, lo que de él se paga en otra moneda
 * («de $96.00 USD»): la conversión queda a la vista, no solo su resultado.
 */
export function ForeignPart({ money, className = "" }: { money: Money; className?: string }) {
  const { t, i18n } = useTranslation("common");
  const foreign = money.mxn === null ? null : foreignLabel(money, i18n?.language);
  if (!foreign) return null;
  return (
    <span className={`block whitespace-nowrap text-[11px] font-normal ${MONEY_PRIVACY_CLASS} ${className}`} data-testid="money-foreign-part">
      {t("workHours.fx.converted", { amount: foreign })}
    </span>
  );
}

/** Subtotal por persona de un grupo de líneas. */
export function PeopleSubtotals({ people, costs, tc }: { people: PersonSubtotal[]; costs: boolean; tc: ThemeClasses }) {
  const { t } = useTranslation("common");
  if (people.length < 2) return null;
  return (
    <table className="mb-2 w-full text-xs" data-testid="ledger-people">
      <thead>
        <tr className={`text-left ${tc.textSecondary}`}>
          <th className="py-1">{t("workHours.ledger.byPerson")}</th>
          <th className="py-1 text-right">{t("workHours.ledger.lines")}</th>
          <th className="py-1 text-right">{t("workHours.kpi.hours")}</th>
          {costs && <th className="py-1 text-right">{t("workHours.ledger.cost")}</th>}
        </tr>
      </thead>
      <tbody>
        {people.map((p) => (
          <tr key={p.userId} className={`border-t ${tc.border} ${tc.text}`}>
            <td className="py-1">{p.userName ?? p.userId}</td>
            <td className={`py-1 text-right ${tc.textSecondary}`}>{p.lines}</td>
            <td className="py-1 text-right font-semibold">{hoursOf(p.minutes)}</td>
            {costs && (
              <td className="py-1 text-right">
                <MoneyText money={p.money} />
                <ForeignPart money={p.money} className={tc.textSecondary} />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ORIGIN_TONES: Record<CountedLine["origin"], string> = {
  manual: "bg-amber-500/15 text-amber-600",
  commits: "bg-emerald-500/15 text-emerald-600",
  crm: "bg-sky-500/15 text-sky-600",
  estimate: "bg-gray-400/15 text-gray-500",
  support: "bg-gray-400/15 text-gray-500",
};

interface CountedLinesTableProps {
  lines: CountedLine[];
  costs: boolean;
  catalog: readonly ActivityType[] | null;
  tc: ThemeClasses;
  /** Columnas que sobran cuando el grupo ya las fija. */
  hideRepo?: boolean;
  hideKind?: boolean;
  testId?: string;
}

export const LEDGER_PAGE = 100;

/**
 * Las líneas que suman un total: una por entrada registrada o por día estimado.
 * Dice quién, cuándo trabajó, cuándo lo registró, de dónde sale (a mano, con
 * commits, del CRM o estimado) y cuánto costó.
 */
export function CountedLinesTable({ lines, costs, catalog, tc, hideRepo = false, hideKind = false, testId }: CountedLinesTableProps) {
  const { t, i18n } = useTranslation("common");
  const locale = localeOf(i18n?.language);
  const shown = lines.slice(0, LEDGER_PAGE);
  const total = sumCost(lines.map((l) => l.cost));
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className={`text-left ${tc.textSecondary}`}>
            <th className="py-1 pr-2">{t("workHours.ledger.date")}</th>
            <th className="py-1 pr-2">{t("workHours.ledger.person")}</th>
            <th className="py-1 pr-2">{t("workHours.ledger.what")}</th>
            <th className="py-1 pr-2">{t("workHours.ledger.loggedAt")}</th>
            <th className="py-1 text-right">{t("workHours.kpi.hours")}</th>
            {costs && <th className="py-1 pl-2 text-right">{t("workHours.ledger.cost")}</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((l) => {
            const native = nativeLabel(l.cost, i18n?.language);
            return (
              <tr key={l.key} className={`border-t align-top ${tc.border} ${tc.text}`}>
                <td className="whitespace-nowrap py-1 pr-2">{l.date}</td>
                <td className="py-1 pr-2">{l.userName ?? l.userId}</td>
                <td className="py-1 pr-2">
                  <span className={`mr-1 inline-block whitespace-nowrap rounded px-1 py-0.5 text-[10px] font-medium ${ORIGIN_TONES[l.origin]}`}>{t(`workHours.ledger.origin.${l.origin}`)}</span>
                  {l.note ?? (l.origin === "estimate" ? t("workHours.ledger.estimateNote", { count: l.commitCount }) : l.origin === "support" ? t("workHours.ledger.supportNote") : t("workHours.ledger.noNote"))}
                  <span className={`block text-[11px] ${tc.textSecondary}`}>
                    {[
                      hideRepo ? null : l.repo ? (l.repo.split("/")[1] ?? l.repo) : t("workHours.repos.noRepo"),
                      hideKind || l.origin === "estimate" || l.origin === "support" ? null : kindLabel(l.kind, catalog, t, i18n?.language),
                      l.origin === "commits" ? t("workHours.ledger.commitsCited", { count: l.commitCount }) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </td>
                <td className={`whitespace-nowrap py-1 pr-2 ${tc.textSecondary}`}>{l.createdAt ? dateTimeLabel(l.createdAt, locale) : "—"}</td>
                <td className="whitespace-nowrap py-1 text-right font-semibold">{hoursOf(l.minutes)}</td>
                {costs && (
                  <td className="whitespace-nowrap py-1 pl-2 text-right">
                    <MoneyText money={sumCost([l.cost])} />
                    {native && <span className={`block text-[11px] ${MONEY_PRIVACY_CLASS} ${tc.textSecondary}`}>{native}</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={`border-t font-semibold ${tc.border} ${tc.text}`}>
            <td className="py-1" colSpan={4}>
              {t("workHours.ledger.total", { count: lines.length })}
            </td>
            <td className="py-1 text-right">{hoursOf(lines.reduce((n, l) => n + l.minutes, 0))}</td>
            {costs && (
              <td className="py-1 pl-2 text-right">
                <MoneyText money={total} />
                <ForeignPart money={total} className={tc.textSecondary} />
              </td>
            )}
          </tr>
        </tfoot>
      </table>
      {lines.length > shown.length && <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{t("workHours.ledger.truncated", { shown: shown.length, total: lines.length })}</p>}
    </div>
  );
}
